import { NgIf, NgTemplateOutlet } from '@angular/common';
import * as i0 from '@angular/core';
import { Directive, inject, ChangeDetectorRef, DestroyRef, TemplateRef, Component, ChangeDetectionStrategy, ViewChild, ContentChild, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tuiParentAnimation } from '@taiga-ui/core/animations';
import { TuiLoader } from '@taiga-ui/core/components/loader';
import { timer } from 'rxjs';
const _c0 = ["wrapper"];
const _c1 = ["*"];
function TuiExpandComponent_ng_container_2_tui_loader_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "tui-loader", 4);
    i0.ɵɵelementContainer(1, 5);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("overlay", true)("showLoader", ctx_r0.loading);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r0.content);
  }
}
function TuiExpandComponent_ng_container_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵprojection(1);
    i0.ɵɵtemplate(2, TuiExpandComponent_ng_container_2_tui_loader_2_Template, 2, 3, "tui-loader", 3);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngIf", ctx_r0.async)("ngIfElse", ctx_r0.content);
  }
}
let TuiExpandContent = /*#__PURE__*/(() => {
  class TuiExpandContent {
    static {
      this.ɵfac = function TuiExpandContent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TuiExpandContent)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: TuiExpandContent,
        selectors: [["", "tuiExpandContent", ""]]
      });
    }
  }
  return TuiExpandContent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const State = {
  Idle: 0,
  Loading: 1,
  Prepared: 2,
  Animated: 3
};
const LOADER_HEIGHT = 48;
/**
 * An event indicating that async data for expand has finished loading.
 * Dispatch to finish loading states for {@link TuiExpandComponent}.
 */
const TUI_EXPAND_LOADED = 'tui-expand-loaded';
let TuiExpandComponent = /*#__PURE__*/(() => {
  class TuiExpandComponent {
    constructor() {
      this.cdr = inject(ChangeDetectorRef);
      this.destroyRef = inject(DestroyRef);
      this.state = State.Idle;
      this.content = null;
      this.expanded = null;
      this.async = false;
    }
    set expandedSetter(expanded) {
      if (this.expanded === null) {
        this.expanded = expanded;
        return;
      }
      if (this.state !== State.Idle) {
        this.expanded = expanded;
        this.state = State.Animated;
        return;
      }
      this.expanded = expanded;
      this.retrigger(this.async && expanded ? State.Loading : State.Animated);
    }
    get contentVisible() {
      return this.expanded || this.state !== State.Idle;
    }
    get overflow() {
      return this.state !== State.Idle;
    }
    get loading() {
      return !!this.expanded && this.async && this.state === State.Loading;
    }
    get height() {
      const {
        expanded,
        state,
        contentWrapper
      } = this;
      if (expanded && state === State.Prepared || !expanded && state === State.Animated) {
        return 0;
      }
      if (contentWrapper && (!expanded && state === State.Prepared || expanded && state === State.Animated)) {
        return contentWrapper.nativeElement.offsetHeight;
      }
      if (contentWrapper && expanded && state === State.Loading) {
        return Math.max(contentWrapper.nativeElement.offsetHeight, LOADER_HEIGHT);
      }
      return null;
    }
    onTransitionEnd({
      propertyName,
      pseudoElement
    }) {
      if (propertyName === 'opacity' && !pseudoElement && this.state === State.Animated) {
        this.state = State.Idle;
      }
    }
    onExpandLoaded(event) {
      event.stopPropagation();
      if (this.state === State.Loading) {
        this.retrigger(State.Animated);
      }
    }
    retrigger(state) {
      this.state = State.Prepared;
      timer(0).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        // We need delay to re-trigger CSS height transition from the correct number
        if (this.state !== State.Prepared) {
          return;
        }
        this.state = state;
        this.cdr.markForCheck();
      });
    }
    static {
      this.ɵfac = function TuiExpandComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TuiExpandComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: TuiExpandComponent,
        selectors: [["tui-expand"]],
        contentQueries: function TuiExpandComponent_ContentQueries(rf, ctx, dirIndex) {
          if (rf & 1) {
            i0.ɵɵcontentQuery(dirIndex, TuiExpandContent, 5, TemplateRef);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.content = _t.first);
          }
        },
        viewQuery: function TuiExpandComponent_Query(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵviewQuery(_c0, 5);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.contentWrapper = _t.first);
          }
        },
        hostVars: 9,
        hostBindings: function TuiExpandComponent_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("transitionend.self", function TuiExpandComponent_transitionend_self_HostBindingHandler($event) {
              return ctx.onTransitionEnd($event);
            })("tui-expand-loaded", function TuiExpandComponent_tui_expand_loaded_HostBindingHandler($event) {
              return ctx.onExpandLoaded($event);
            });
          }
          if (rf & 2) {
            i0.ɵɵattribute("aria-expanded", ctx.expanded);
            i0.ɵɵstyleProp("height", ctx.height, "px");
            i0.ɵɵclassProp("_loading", ctx.loading)("_overflow", ctx.overflow)("_expanded", ctx.expanded);
          }
        },
        inputs: {
          async: "async",
          expandedSetter: [0, "expanded", "expandedSetter"]
        },
        ngContentSelectors: _c1,
        decls: 3,
        vars: 3,
        consts: [["wrapper", ""], [1, "t-wrapper"], [4, "ngIf"], ["size", "l", 3, "overlay", "showLoader", 4, "ngIf", "ngIfElse"], ["size", "l", 3, "overlay", "showLoader"], [3, "ngTemplateOutlet"]],
        template: function TuiExpandComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵelementStart(0, "div", 1, 0);
            i0.ɵɵtemplate(2, TuiExpandComponent_ng_container_2_Template, 3, 2, "ng-container", 2);
            i0.ɵɵelementEnd();
          }
          if (rf & 2) {
            i0.ɵɵproperty("@tuiParentAnimation", undefined)("@.disabled", ctx.overflow);
            i0.ɵɵadvance(2);
            i0.ɵɵproperty("ngIf", ctx.contentVisible);
          }
        },
        dependencies: [NgIf, NgTemplateOutlet, TuiLoader],
        styles: ["[_nghost-%COMP%]{transition-property:opacity,height,visibility;transition-duration:var(--tui-duration, .3s);transition-timing-function:ease-in-out;display:block;opacity:0;transition-delay:1ms}._overflow[_nghost-%COMP%]{overflow:hidden}._expanded[_nghost-%COMP%]{opacity:1}._loading[_nghost-%COMP%]{opacity:.99}.t-wrapper[_ngcontent-%COMP%]:before, .t-wrapper[_ngcontent-%COMP%]:after{content:\"\";display:table}"],
        data: {
          animation: [tuiParentAnimation]
        },
        changeDetection: 0
      });
    }
  }
  return TuiExpandComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const TuiExpand = [TuiExpandComponent, TuiExpandContent];

/**
 * Generated bundle index. Do not edit.
 */

export { TUI_EXPAND_LOADED, TuiExpand, TuiExpandComponent, TuiExpandContent };
